import {
  analyticsApi,
  projectApi,
  type AnalyticsRangeSelection,
  type AnalyticsSummary,
  type PortfolioProjectAnalytics,
  type Project,
} from '@shared/api-client';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, FolderPlus, Pin, Search } from 'lucide-react';
import * as React from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useSession } from '@/hooks/use-session';
import { ProjectAvatar } from '@/components/project-avatar';
import { ChangeIndicator } from '@/components/analytics/change-indicator';
import { AnalyticsRangeSelect } from '@/components/analytics/range-select';
import { LivePulse } from '@/components/analytics/live-pulse';
import { Sparkline } from '@/components/analytics/sparkline';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { usePinnedProjects } from '@/hooks/use-pinned-projects';
import { partitionPinnedProjects } from '@/lib/pinned-projects';
import { calculatePercentChange, cn, formatCompactNumber } from '@/lib/utils';

type SortMode = 'visitors' | 'change' | 'recent' | 'alphabetical';

const EMPTY_SUMMARY: AnalyticsSummary = {
  visitors: 0,
  previousVisitors: 0,
  sessions: 0,
  previousSessions: 0,
  pageviews: 0,
  previousPageviews: 0,
  events: 0,
  previousEvents: 0,
  customEvents: 0,
  previousCustomEvents: 0,
  identifiedUsers: 0,
  previousIdentifiedUsers: 0,
  currentVisitors: 0,
  lastEventAt: null,
};

const statusFor = (summary: AnalyticsSummary) => {
  if (summary.events === 0) {
    return { label: 'No data in range', tone: 'neutral' as const };
  }
  if (summary.currentVisitors > 0) {
    return { label: 'Live', tone: 'success' as const };
  }
  return { label: 'Idle', tone: 'neutral' as const };
};

const ProjectCard = ({
  project,
  analytics,
  pinned,
  onTogglePinned,
  comparisonEnabled = true,
}: {
  project: Project;
  analytics: PortfolioProjectAnalytics | undefined;
  pinned: boolean;
  onTogglePinned: () => void;
  comparisonEnabled?: boolean;
}) => {
  const summary = analytics?.summary ?? EMPTY_SUMMARY;
  const status = statusFor(summary);

  return (
    <Card className="group relative overflow-hidden shadow-none transition-colors hover:border-border-strong">
      <Link
        to={`/projects/${encodeURIComponent(project.id)}`}
        className="absolute inset-0 z-0 rounded-card"
        aria-label={`Open ${project.name}`}
      />

      <div className="pointer-events-none relative z-10 p-5">
        <div className="flex items-start gap-3">
          <ProjectAvatar
            name={project.name}
            logoUrl={project.profile?.logoUrl}
            className="size-9"
          />

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-sm font-semibold text-ink">
                {project.name}
              </h2>
              <Badge tone={status.tone} className="hidden sm:inline-flex">
                {status.label}
              </Badge>
            </div>
            <p className="mt-0.5 truncate text-xs text-ink-subtle">
              {project.primaryDomain ?? 'No primary domain'}
            </p>
          </div>

          <button
            type="button"
            onClick={onTogglePinned}
            className={cn(
              'pointer-events-auto relative z-20 flex size-8 items-center justify-center rounded-md transition-colors',
              pinned
                ? 'text-accent hover:bg-accent-subtle'
                : 'text-ink-subtle opacity-60 hover:bg-surface-muted hover:text-ink group-hover:opacity-100',
            )}
            aria-label={
              pinned ? `Unpin ${project.name}` : `Pin ${project.name}`
            }
            aria-pressed={pinned}
          >
            <Pin className="size-4" fill={pinned ? 'currentColor' : 'none'} />
          </button>
        </div>

        <Sparkline
          values={analytics?.timeseries.map((point) => point.visitors) ?? []}
          className="mt-5"
        />

        <div className="mt-3 flex items-end justify-between gap-4">
          <div>
            <p className="text-2xl font-semibold tracking-tight text-ink tabular-nums">
              {formatCompactNumber(summary.visitors)}
            </p>
            <p className="mt-0.5 text-xs text-ink-muted">visitors</p>
          </div>
          {comparisonEnabled && (
            <ChangeIndicator
              current={summary.visitors}
              previous={summary.previousVisitors}
              className="mb-0.5"
            />
          )}
        </div>

        {summary.events > 0 ? (
          <Link
            to={`/projects/${encodeURIComponent(project.id)}/realtime`}
            className="pointer-events-auto relative z-20 mt-4 flex items-center justify-between gap-3 border-t border-border pt-3 text-xs font-medium text-ink hover:text-accent"
            aria-label={`View ${summary.currentVisitors} active visitors for ${project.name}`}
          >
            <span className="inline-flex items-center gap-1.5">
              <LivePulse active={summary.currentVisitors > 0} />
              {summary.currentVisitors} active now
            </span>
            <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        ) : (
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-3 text-xs text-ink-subtle">
            <span>No activity yet</span>
            <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </div>
        )}
      </div>
    </Card>
  );
};

export const ProjectsPage = () => {
  const { user } = useSession();
  const [searchParams] = useSearchParams();
  const { pinned, togglePinned } = usePinnedProjects();
  const [range, setRange] = React.useState<AnalyticsRangeSelection>({
    key: '7d',
  });
  const [search, setSearch] = React.useState('');
  const [sort, setSort] = React.useState<SortMode>('visitors');
  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: projectApi.listProjects,
  });
  const analyticsQuery = useQuery({
    queryKey: ['analytics', 'portfolio', range],
    queryFn: () => analyticsApi.getPortfolio(range),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

  const analyticsByProject = new Map(
    analyticsQuery.data?.projects.map((project) => [
      project.projectId,
      project,
    ]) ?? [],
  );
  const projects = [...(projectsQuery.data?.projects ?? [])]
    .filter((project) => {
      const term = search.trim().toLowerCase();
      return (
        !term ||
        project.name.toLowerCase().includes(term) ||
        project.primaryDomain?.toLowerCase().includes(term)
      );
    })
    .sort((left, right) => {
      const leftPinned = pinned.includes(left.id);
      const rightPinned = pinned.includes(right.id);
      if (leftPinned !== rightPinned) {
        return leftPinned ? -1 : 1;
      }

      const leftSummary =
        analyticsByProject.get(left.id)?.summary ?? EMPTY_SUMMARY;
      const rightSummary =
        analyticsByProject.get(right.id)?.summary ?? EMPTY_SUMMARY;

      if (sort === 'alphabetical') {
        return left.name.localeCompare(right.name);
      }
      if (sort === 'recent') {
        return (
          new Date(rightSummary.lastEventAt ?? 0).getTime() -
          new Date(leftSummary.lastEventAt ?? 0).getTime()
        );
      }
      if (sort === 'change') {
        const leftChange = calculatePercentChange(
          leftSummary.visitors,
          leftSummary.previousVisitors,
        );
        const rightChange = calculatePercentChange(
          rightSummary.visitors,
          rightSummary.previousVisitors,
        );
        return Math.abs(rightChange ?? 100) - Math.abs(leftChange ?? 100);
      }
      return rightSummary.visitors - leftSummary.visitors;
    });

  const projectsWithData = analyticsQuery.data?.projects.filter(
    (project) => project.summary.events > 0,
  ).length;
  const projectGroups = partitionPinnedProjects(projects, pinned);
  const projectCards = (items: Project[]) => (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {items.map((project) => (
        <ProjectCard
          comparisonEnabled={
            analyticsQuery.data?.range.comparisonEnabled !== false
          }
          key={project.id}
          project={project}
          analytics={analyticsByProject.get(project.id)}
          pinned={pinned.includes(project.id)}
          onTogglePinned={() => togglePinned(project.id)}
        />
      ))}
    </div>
  );

  const firstProject =
    projectsQuery.data?.projects.length === 1
      ? projectsQuery.data.projects[0]
      : null;
  if (
    firstProject?.profile?.setupStep === 'identity' &&
    searchParams.get('explore') !== '1' &&
    ['owner', 'admin'].includes(user?.currentOrganization.role ?? '')
  ) {
    return <Navigate to={`/projects/${firstProject.id}/setup`} replace />;
  }

  return (
    <div className="space-y-7">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink">
            Projects
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            {projectsQuery.data && analyticsQuery.data
              ? `${projectsQuery.data.projects.length} projects · ${projectsWithData ?? 0} with data`
              : projectsQuery.data
                ? `${projectsQuery.data.projects.length} projects`
                : 'Every product and its complete customer journey.'}
          </p>
        </div>

        <Button asChild>
          <Link to="/projects/new">
            <FolderPlus /> Add project
          </Link>
        </Button>
      </div>

      <div className="flex flex-col justify-between gap-3 border-y border-border py-4 md:flex-row md:items-center">
        <div className="relative w-full md:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search projects"
            aria-label="Search projects"
            className="pl-9"
          />
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <AnalyticsRangeSelect value={range} onValueChange={setRange} />
          <Select
            value={sort}
            onValueChange={(value) => setSort(value as SortMode)}
          >
            <SelectTrigger
              className="w-full sm:w-40"
              aria-label="Sort projects"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="visitors">Most visitors</SelectItem>
              <SelectItem value="change">Biggest change</SelectItem>
              <SelectItem value="recent">Recently active</SelectItem>
              <SelectItem value="alphabetical">Alphabetical</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {analyticsQuery.isError ? (
        <Alert tone="error">
          Project analytics could not be loaded. Project management is still
          available.
        </Alert>
      ) : null}

      {projectsQuery.isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-72 rounded-card" />
          ))}
        </div>
      ) : (projectsQuery.data?.projects.length ?? 0) === 0 ? (
        <Card className="p-10 text-center shadow-none">
          <p className="text-sm font-medium text-ink">No projects yet</p>
          <p className="mt-1 text-sm text-ink-muted">
            Add a product to start collecting its customer journey.
          </p>
          <Button asChild className="mt-5">
            <Link to="/projects/new">
              <FolderPlus /> Add project
            </Link>
          </Button>
        </Card>
      ) : projects.length === 0 ? (
        <Card className="p-10 text-center shadow-none">
          <p className="text-sm font-medium text-ink">No matching projects</p>
          <p className="mt-1 text-sm text-ink-muted">
            Try another name or domain.
          </p>
        </Card>
      ) : (
        <div className="space-y-7">
          {projectGroups.pinned.length > 0 ? (
            <section aria-labelledby="pinned-projects-heading">
              <div className="mb-3 flex items-center gap-2">
                <Pin className="size-4 text-accent" fill="currentColor" />
                <h2
                  id="pinned-projects-heading"
                  className="text-sm font-semibold text-ink"
                >
                  Pinned
                </h2>
                <span className="text-xs text-ink-subtle">
                  {projectGroups.pinned.length}
                </span>
              </div>
              {projectCards(projectGroups.pinned)}
            </section>
          ) : null}

          {projectGroups.others.length > 0 ? (
            <section aria-labelledby="other-projects-heading">
              {projectGroups.pinned.length > 0 ? (
                <h2
                  id="other-projects-heading"
                  className="mb-3 text-sm font-semibold text-ink-muted"
                >
                  Other projects
                </h2>
              ) : null}
              {projectCards(projectGroups.others)}
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
};
